import type { AuthUserDTO } from "@platform/shared-types";
import { setNevisignAuthProvider } from "@nevisign/reader-core";
import { useAuthStore } from "../stores/auth-store";
import {
  accessTokenExpiresWithinMs,
  getJwtExpiryMs,
} from "./jwt-expiry";
import { notifySmsFromHeaders } from "./sms-notice";

const API_URL = (import.meta.env.VITE_API_URL?.trim() || "/api/v1").replace(
  /\/$/,
  "",
);

const REFRESH_BEFORE_MS = 90_000;
const SESSION_EXPIRED_MSG = "نشست شما منقضی شده است. لطفاً دوباره وارد شوید.";

export class AuthApiError extends Error {
  status: number;
  code: string;

  constructor(message: string, status = 0, code = "AUTH_ERROR") {
    super(message);
    this.name = "AuthApiError";
    this.status = status;
    this.code = code;
  }
}

export interface LoginResponse {
  accessToken: string;
  user: AuthUserDTO;
}

export type OtpRequestResponse = {
  ok: true;
  phone: string;
  expiresInSeconds: number;
  debugCode?: string;
  smsPreview?: { phone: string; message: string; purpose: string };
};

export type OtpVerifyResult =
  | LoginResponse
  | {
      needsOrganizationPick: true;
      organizations: Array<{
        id: string;
        name: string;
        slug: string;
        role: string;
        userId: string;
        isPersonal?: boolean;
      }>;
    };

let refreshPromise: Promise<string | null> | null = null;
let refreshTimer: ReturnType<typeof setTimeout> | null = null;

function apiOrigin(): string {
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin;
  }
  return "http://127.0.0.1:5174";
}

function authUrl(path: string): string {
  return new URL(`${API_URL}${path}`, apiOrigin()).toString();
}

function userFacingAuthMessage(
  path: string,
  status: number,
  data: { error?: string | { message?: string; code?: string }; message?: string } | undefined,
): string {
  const nested =
    data?.error && typeof data.error === "object" && typeof data.error.message === "string"
      ? data.error.message.trim()
      : "";
  const raw =
    nested ||
    (typeof data?.message === "string" && data.message.trim()) ||
    (typeof data?.error === "string" && data.error.trim()) ||
    "";
  if (/^no request id found$/i.test(raw)) {
    return path.includes("/otp/")
      ? "پاسخ مرحلهٔ ورود ناقص بود. لطفاً دوباره کد را درخواست کنید."
      : "پاسخ ورود از سرور ناقص بود. لطفاً دوباره تلاش کنید.";
  }
  // Bad credentials are 401 — do not collapse them into a generic session-expired string.
  if (!raw && status === 401 && (path.includes("/login") || path.includes("/otp/"))) {
    return "ایمیل یا رمز عبور نادرست است";
  }
  return raw || (status === 401 ? SESSION_EXPIRED_MSG : `خطای ورود (HTTP ${status})`);
}

async function parseJson<T>(res: Response): Promise<T> {
  const text = await res.text();
  if (!text) return undefined as T;
  try {
    return JSON.parse(text) as T;
  } catch {
    throw new AuthApiError("پاسخ نامعتبر از سرور", res.status, "INVALID_RESPONSE");
  }
}

export async function refreshAccessToken(): Promise<string | null> {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      try {
        const res = await fetch(authUrl("/auth/refresh"), {
          method: "POST",
          credentials: "include",
        });
        if (!res.ok) return null;
        const data = await parseJson<LoginResponse>(res);
        if (!data?.accessToken) return null;
        useAuthStore.getState().setSession(data.accessToken, data.user);
        scheduleProactiveRefresh();
        return data.accessToken;
      } catch {
        return null;
      } finally {
        refreshPromise = null;
      }
    })();
  }
  return refreshPromise;
}

function scheduleProactiveRefresh() {
  if (typeof window === "undefined") return;
  if (refreshTimer) clearTimeout(refreshTimer);
  const token = useAuthStore.getState().accessToken;
  const exp = getJwtExpiryMs(token);
  if (!exp) return;
  const delay = Math.max(0, exp - Date.now() - REFRESH_BEFORE_MS);
  refreshTimer = setTimeout(() => {
    void ensureFreshAccessToken();
  }, delay);
}

export async function ensureFreshAccessToken(): Promise<string | null> {
  const token = useAuthStore.getState().accessToken;
  if (!token) return null;
  if (!accessTokenExpiresWithinMs(token, REFRESH_BEFORE_MS)) {
    scheduleProactiveRefresh();
    return token;
  }
  const refreshed = await refreshAccessToken();
  if (refreshed) return refreshed;
  // Do not keep attaching an already-expired JWT — API then returns a cryptic
  // «توکن احراز هویت نامعتبر یا منقضی است» instead of a clean login prompt.
  if (accessTokenExpiresWithinMs(token, 0)) {
    return null;
  }
  return token;
}

export function wireReaderAuthProvider() {
  setNevisignAuthProvider(async () => ({
    token: await ensureFreshAccessToken(),
    credentials: "include",
  }));
  scheduleProactiveRefresh();
  useAuthStore.subscribe((state, prev) => {
    if (state.accessToken !== prev.accessToken) scheduleProactiveRefresh();
  });
}

async function authRequest<T>(
  path: string,
  options: { method?: string; body?: unknown; withAuth?: boolean } = {},
): Promise<T> {
  if (options.withAuth) await ensureFreshAccessToken();
  const token = useAuthStore.getState().accessToken;
  const res = await fetch(authUrl(path), {
    method: options.method ?? "POST",
    credentials: "include",
    headers: {
      Accept: "application/json",
      // OriginRef (arch-13 §10): signups from the Nevisign app count as the
      // managers' assistant surface.
      "X-Nevisign-Surface": "dastyar",
      ...(options.body ? { "Content-Type": "application/json" } : {}),
      ...(token && options.withAuth !== false
        ? { Authorization: `Bearer ${token}` }
        : {}),
    },
    body: options.body ? JSON.stringify(options.body) : undefined,
  });
  notifySmsFromHeaders(res.headers);
  const data = await parseJson<T & { error?: string; message?: string }>(res);
  if (!res.ok) {
    const message = userFacingAuthMessage(path, res.status, data);
    throw new AuthApiError(message, res.status);
  }
  return data as T;
}

export async function loginWithEmail(input: {
  email: string;
  password: string;
}): Promise<LoginResponse> {
  const data = await authRequest<LoginResponse>("/auth/login", { body: input });
  useAuthStore.getState().setSession(data.accessToken, data.user);
  return data;
}

export async function requestOtp(phone: string): Promise<OtpRequestResponse> {
  return authRequest<OtpRequestResponse>("/auth/otp/request", {
    body: { phone },
    withAuth: false,
  });
}

export async function verifyOtp(input: {
  phone: string;
  code: string;
  organizationId?: string;
}): Promise<OtpVerifyResult> {
  const data = await authRequest<OtpVerifyResult>("/auth/otp/verify", {
    body: input,
    withAuth: false,
  });
  if ("accessToken" in data && data.accessToken) {
    useAuthStore.getState().setSession(data.accessToken, data.user);
  }
  return data;
}

export async function orgLogin(_input: {
  nationalId: string;
  password: string;
}): Promise<LoginResponse> {
  throw new AuthApiError(
    "ورود شخص حقوقی به اپلیکیشن خواننده مجاز نیست. لطفاً با شماره موبایل (شخص حقیقی) وارد شوید.",
    403,
  );
}

export async function logout(): Promise<void> {
  try {
    await authRequest("/auth/logout", { withAuth: true });
  } catch {
    /* still clear local session */
  } finally {
    useAuthStore.getState().clearSession();
  }
}
